import { api } from "../lib/api";
import type {
	CreateEventInput,
	CurrentStep,
	Event,
	UpdateEventInput,
} from "../types/event";

/**
 * Phase 37 events service — live `/api/events` wiring.
 *
 * Surface:
 *   - getEventsInRange(from, to) → GET /api/events?from=&to= → flat Event[] (D-01, D-02)
 *   - createEvent(body, idempotencyKey) → POST /api/events with Idempotency-Key header (C-07 / D-04)
 *   - updateEvent(id, patch) → PUT /api/events/:id (caller passes BARE UUID; synthetic ids
 *     fail loud at the server with 400 WHOLE_SERIES_ONLY — RESEARCH Pitfall 2)
 *   - deleteEvent(id) → DELETE /api/events/:id (soft-delete server-side — C-11)
 *   - getCurrentStep() → GET /api/current-step (Phase 35-03 endpoint, unchanged)
 *
 * Dev-only error simulator: when the `VITE_MOCK_API_ERROR` Vite env var is set
 * to "1" (build-time) or `localStorage.mockApiError=1` (runtime, dev-only),
 * `getEventsInRange` throws synchronously so QA can exercise FullPageErrorState.
 * Stripped from production bundles via `import.meta.env.MODE` check.
 *
 * Capacitor-safe: NO `window.location.*` reads (CLAUDE.md prohibition — under
 * `capacitor://` schemes `new URL(window.location.href)` may throw and the
 * rule is unconditional regardless of `MODE`).
 */

function isProdBuild(): boolean {
	// Vite env check — strips error simulator from production bundles.
	return import.meta.env.MODE === "production";
}

function shouldSimulateError(): boolean {
	if (isProdBuild()) return false;
	if (import.meta.env.VITE_MOCK_API_ERROR === "1") return true;
	if (typeof window === "undefined") return false;
	try {
		if (window.localStorage.getItem("mockApiError") === "1") return true;
	} catch {
		// localStorage may throw in private mode
	}
	return false;
}

/**
 * GET /api/events?from=&to= — returns flat Event[].
 *
 * Server response envelope is `{ events: Event[]; truncated: boolean }`; the
 * `truncated` field is silently dropped (D-02 — out of scope for v1.11 UI).
 * `from` and `to` are ISO date or datetime strings.
 */
export async function getEventsInRange(
	from: string,
	to: string,
): Promise<Event[]> {
	if (shouldSimulateError()) {
		throw new Error("Mock API error (mockError flag active)");
	}
	const response = await api.get<{ events: Event[]; truncated: boolean }>(
		"/api/events",
		{ params: { from, to } },
	);
	return response.data.events;
}

/**
 * POST /api/events — create a new patient event.
 *
 * Caller MUST pass a fresh UUIDv4 `idempotencyKey` per save attempt (held in `useRef`
 * across automatic retries — RESEARCH Pattern 2). Server enforces `source='patient'`
 * from JWT; FE never sends `source` (era-api `createEventSchema` rejects extra fields).
 */
export async function createEvent(
	body: CreateEventInput,
	idempotencyKey: string,
): Promise<Event> {
	const response = await api.post<{ event: Event }>("/api/events", body, {
		headers: { "Idempotency-Key": idempotencyKey },
	});
	return response.data.event;
}

/**
 * PUT /api/events/:id — patch an existing event.
 *
 * `id` MUST be a bare UUID. Caller is responsible for extracting the base id via
 * `parseSyntheticEventId(event.id)?.baseId ?? event.id` before calling — service does
 * NOT silently strip synthetic ids (RESEARCH Pitfall 2: fail-loud at server with 400
 * WHOLE_SERIES_ONLY is preferable to silent acceptance on a recurring series).
 * EDM-05: edit operates whole-series for v1.11.
 */
export async function updateEvent(
	id: string,
	patch: UpdateEventInput,
): Promise<Event> {
	const response = await api.put<{ event: Event }>(`/api/events/${id}`, patch);
	return response.data.event;
}

/**
 * DELETE /api/events/:id — soft-delete (server sets `deletedAt`).
 *
 * `id` MUST be a bare UUID (caller extracts baseId — RESEARCH Pitfall 2).
 * Server returns `{ ok: true }`; body is discarded.
 * EDM-05: delete operates whole-series for v1.11.
 */
export async function deleteEvent(id: string): Promise<void> {
	await api.delete(`/api/events/${id}`);
}

/**
 * GET /api/current-step — real Phase 35-03 endpoint.
 * FE id-to-asset override happens at the hook/component layer (D-04).
 */
export async function getCurrentStep(): Promise<CurrentStep> {
	const response = await api.get<CurrentStep>("/api/current-step");
	return response.data;
}
